import { supabase } from '../config/supabase';

export async function cancelTrip(tripId: string, cancelledReason: string): Promise<void> {
  const reason = cancelledReason.trim();
  if (!reason) {
    throw new Error('Cancellation reason is required');
  }

  const { error } = await supabase
    .from('trips')
    .update({
      status: 'cancelled',
      cancelled_reason: reason,
      cancelled_at: new Date().toISOString(),
    })
    .eq('id', tripId);

  if (error) {
    throw new Error(error.message);
  }
}

export async function fetchSystemSettings(): Promise<{
  gps_report_interval: number;
  delay_threshold_minutes: number;
  maintenance_reminder_days: number;
  travel_time_mode: 'static' | 'directions';
  static_average_speed_kmh: number;
  travel_road_factor: number;
  latest_start_offset_minutes: number;
}> {
  const defaults = {
    gps_report_interval: 120,
    delay_threshold_minutes: 30,
    maintenance_reminder_days: 90,
    travel_time_mode: 'static' as const,
    static_average_speed_kmh: 35,
    travel_road_factor: 1.25,
    latest_start_offset_minutes: 30,
  };

  function parseValue(v: Record<string, unknown>) {
    return {
      gps_report_interval: typeof v.gps_report_interval === 'number' ? v.gps_report_interval : defaults.gps_report_interval,
      delay_threshold_minutes: typeof v.delay_threshold_minutes === 'number' ? v.delay_threshold_minutes : defaults.delay_threshold_minutes,
      maintenance_reminder_days: typeof v.maintenance_reminder_days === 'number' ? v.maintenance_reminder_days : defaults.maintenance_reminder_days,
      travel_time_mode: v.travel_time_mode === 'directions' ? 'directions' as const : 'static' as const,
      static_average_speed_kmh: typeof v.static_average_speed_kmh === 'number' ? v.static_average_speed_kmh : defaults.static_average_speed_kmh,
      travel_road_factor: typeof v.travel_road_factor === 'number' ? v.travel_road_factor : defaults.travel_road_factor,
      latest_start_offset_minutes:
        typeof v.latest_start_offset_minutes === 'number'
          ? Math.max(0, Math.min(240, Math.round(v.latest_start_offset_minutes)))
          : defaults.latest_start_offset_minutes,
    };
  }

  async function loadFromSettingsTable(): Promise<Record<string, unknown> | null> {
    const { data: adminData } = await supabase
      .from('settings')
      .select('value')
      .eq('key', 'system_settings')
      .maybeSingle();
    if (!adminData?.value || typeof adminData.value !== 'object') return null;
    return adminData.value as Record<string, unknown>;
  }

  const table = await loadFromSettingsTable();
  const { data, error } = await supabase.rpc('get_driver_system_settings');

  if (error || !data || typeof data !== 'object') {
    if (!table) return defaults;
    return parseValue(table);
  }

  // Prefer settings-table values for admin-managed keys (RPC may omit newer fields
  // such as latest_start_offset_minutes).
  return parseValue({ ...(data as Record<string, unknown>), ...(table ?? {}) });
}
